/* Atlas V3 Desktop — the app chrome.
   Two nav layouts (sidebar + topbar) toggled by a reviewer control (persisted
   to localStorage 'atlas_web_nav'); brand, nav items, live mover ribbon, market
   status, Day/Night theme toggle (theme.js), and the ⌘K CommandPalette.
   Renders the active page as children in the scroll area.

   Ported from the standalone mockup; wired to real app infra. */
import { useEffect, useRef, useState } from 'react'
import { getStoredMode, setThemeMode, resolveTheme } from '@/v2/v3/theme.js'
import { marketState, useTicker } from '@/v2/v3/lib'
import { useIsAdmin } from '@/v2/hooks/useIsAdmin'
import { CommandPalette } from './CommandPalette'
import { MoverRibbon } from './MoverRibbon'

// Nav model — mirrors the LIVE mobile labels (Overview · Signals · Markets ·
// My Stocks) plus the desktop-promoted More items (Insights · Calendar ·
// Settings). Icon paths are Tabler Icons outline (MIT, tabler.io/icons) —
// same merged-d strings as the mobile shell's NAV/MORE_ITEMS; keep both in sync.
const ICONS = {
  overview: 'M5 12l-2 0l9 -9l9 9l-2 0M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6',
  signals: 'M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11',
  markets: 'M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14M4 20h14',
  mystocks: 'M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873l-6.158 -3.245',
  insights: 'M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0M3 6l0 13M12 6l0 13M21 6l0 13',
  cycle: 'M21 12h-2c-.894 0 -1.662 -.857 -1.761 -2c-.296 -3.45 -.749 -6 -2.749 -6s-2.5 3.582 -2.5 8s-.5 8 -2.5 8s-2.452 -2.547 -2.749 -6c-.1 -1.147 -.867 -2 -1.763 -2h-2',
  calendar: 'M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12M16 3v4M8 3v4M4 11h16M11 15h1M12 15v3',
  // Tabler `robot` — the agentic account surface.
  agent: 'M6 4m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2zM12 2v2M9 12v9M15 12v9M5 16l4 -2M15 14l4 2M9 18h6M10 8v.01M14 8v.01',
  settings: 'M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0',
  // Tabler `terminal-2` — the admin/ops surface.
  admin: 'M8 9l3 3l-3 3M13 15l3 0M3 4m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z',
}
const PRIMARY = [
  { k: 'overview', label: 'Overview' },
  { k: 'signals', label: 'Signals', count: 'signals' },
  { k: 'markets', label: 'Markets' },
  { k: 'mystocks', label: 'My Stocks', count: 'mystocks' },
]
const MORE = [
  { k: 'insights', label: 'Insights' },
  { k: 'cycle', label: 'Cycle' },
  { k: 'calendar', label: 'Calendar' },
  { k: 'settings', label: 'Settings' },
]

function Icon({ k }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[k]} />
    </svg>
  )
}
function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  )
}
function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
    </svg>
  )
}
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  )
}

export function DesktopShell({ active, onNav, counts = {}, coverage, children }) {
  const openTicker = useTicker()
  // Admin-only nav entry (display gating — the page + server gate for real).
  const isAdmin = useIsAdmin()
  const moreItems = isAdmin ? [...MORE, { k: 'admin', label: 'Admin' }] : MORE
  const [navMode, setNavMode] = useState(() => {
    try { return localStorage.getItem('atlas_web_nav') || 'top' } catch { return 'top' }
  })
  const [night, setNight] = useState(() => resolveTheme(getStoredMode()) === 'night')
  const [searchOpen, setSearchOpen] = useState(false)
  // Live market clock — re-evaluate every 30s so the countdown + OPEN/CLOSED
  // pill actually flip while the tab is open (mirrors mobile's interval).
  const [mkt, setMkt] = useState(() => marketState())
  useEffect(() => {
    const id = setInterval(() => setMkt(marketState()), 30_000)
    return () => clearInterval(id)
  }, [])

  // Land every page switch at the top (the old mockup did this; scrolled
  // positions bleeding across pages read as a glitch).
  const scrollRef = useRef(null)
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0 }, [active])

  // Sliding active-tab underline — one measured indicator instead of a
  // per-item bar that teleports. Remeasures on nav resize.
  const navRef = useRef(null)
  const [ind, setInd] = useState(null) // { left, width } | null
  useEffect(() => {
    if (navMode !== 'top') return
    const nav = navRef.current
    if (!nav) return
    const measure = () => {
      const el = nav.querySelector(`[data-k="${active}"]`)
      if (!el) { setInd(null); return }
      setInd({ left: el.offsetLeft + 14, width: Math.max(0, el.offsetWidth - 28) })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(nav)
    return () => ro.disconnect()
  }, [active, navMode, counts])

  const setMode = (m) => {
    setNavMode(m)
    try { localStorage.setItem('atlas_web_nav', m) } catch { /* private mode */ }
  }
  const toggleTheme = () => {
    const next = night ? 'ink' : 'night'
    setThemeMode(next)
    setNight(next === 'night')
  }

  // ⌘K / Ctrl-K opens the search overlay.
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault(); setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])

  const countFor = (item) => (item.count ? counts[item.count] : undefined)

  const navBtn = (item, cls) => (
    <button
      key={item.k}
      data-k={item.k}
      className={`${cls} ${active === item.k ? 'active' : ''}`}
      onClick={() => onNav(item.k)}
    >
      {cls === 'v3web-navitem' && <Icon k={item.k} />}
      <span>{item.label}</span>
      {countFor(item) != null && <span className="ct">{countFor(item)}</span>}
    </button>
  )

  const themeBtn = (
    <button className="v3web-iconbtn" onClick={toggleTheme} aria-label="Toggle theme">
      {night ? <MoonIcon /> : <SunIcon />}
    </button>
  )

  return (
    <div className="v3web-root">
      <div className="v3web-app">
        {/* Sidebar layout */}
        {navMode === 'side' && (
          <aside className="v3web-sidebar">
            <div className="v3web-brand" onClick={() => onNav('overview')}>
              <div className="mk">Atlas</div>
              <div className="sub">Investment Intelligence</div>
            </div>
            <div className="v3web-search" onClick={() => setSearchOpen(true)}>
              <SearchIcon /><span>Search the universe</span><kbd>⌘K</kbd>
            </div>
            <div className="v3web-navlabel">Navigation</div>
            <nav className="v3web-nav">{PRIMARY.map((i) => navBtn(i, 'v3web-navitem'))}</nav>
            <div className="v3web-navlabel">More</div>
            <nav className="v3web-nav">{moreItems.map((i) => navBtn(i, 'v3web-navitem'))}</nav>
            <div className="v3web-sidefoot">
              {coverage && (
                <div className="v3web-engine">
                  Atlas covers <b>{coverage.tickers}</b> tickers across <b>{coverage.sectors}</b> sectors.
                </div>
              )}
              <div className="v3web-lastrun"><span className="v3web-dotpulse" /> {mkt.label}</div>
              <div className="v3web-ver">v3.0 · stonk.world</div>
            </div>
          </aside>
        )}

        <div className="v3web-colmain">
          {/* Top-nav layout */}
          {navMode === 'top' && (
            <>
              <div className="v3web-topnav">
                <div className="v3web-topnav-inner">
                  <div className="v3web-tnbrand" onClick={() => onNav('overview')}>Atlas</div>
                  <nav ref={navRef} className="v3web-tnnav atlas-scroll">
                    {[...PRIMARY, ...moreItems].map((i) => navBtn(i, 'v3web-tnitem'))}
                    {ind && <span className="v3web-tnind" style={{ left: ind.left, width: ind.width }} />}
                  </nav>
                  <div className="v3web-tnright">
                    <div className="v3web-search v3web-tnsearch" onClick={() => setSearchOpen(true)}>
                      <SearchIcon /><span>Search</span><kbd>⌘K</kbd>
                    </div>
                    <div className="v3web-mstatus"><span className="d" style={{ background: `hsl(var(${mkt.dot}))` }} /> {mkt.open ? 'OPEN' : 'CLOSED'}</div>
                    {themeBtn}
                    <div className="v3web-avatar">B</div>
                  </div>
                </div>
              </div>
              <div className="v3web-ribbonstrip"><div className="v3web-ribbonstrip-inner"><MoverRibbon /></div></div>
            </>
          )}

          {/* Sidebar-layout topbar */}
          {navMode === 'side' && (
            <div className="v3web-topbar">
              <div className="v3web-mstatus">
                <span className="d" style={{ background: `hsl(var(${mkt.dot}))` }} /> {mkt.label}
              </div>
              <MoverRibbon />
              <div className="v3web-tbactions">
                {themeBtn}
                <div className="v3web-avatar">B</div>
              </div>
            </div>
          )}

          <div ref={scrollRef} className="v3web-scroll atlas-scroll">
            <div className="v3web-page">{children}</div>
          </div>
        </div>
      </div>

      {/* Reviewer-only: compare nav layouts */}
      <div className="v3web-navtoggle">
        <span className="lbl">NAV</span>
        <div className="seg">
          <button className={navMode === 'side' ? 'on' : ''} onClick={() => setMode('side')}>Sidebar</button>
          <button className={navMode === 'top' ? 'on' : ''} onClick={() => setMode('top')}>Top bar</button>
        </div>
      </div>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} openTicker={openTicker} onNav={onNav} />
    </div>
  )
}
